<script setup lang="ts">
import { computed } from "vue";

import { useDictsysInflectionSchemeStore } from "@/stores";
import type {
  DictsysArticle,
  DictsysCustomValue,
  DictsysCustomValueType,
  DictsysMeaning,
  DictsysTranslation,
  DictsysWordclass
} from "@/types";

import LeArticleCustomContent from "./LeArticleCustomContent.vue";
import LeArticleGenders from "./LeArticleGenders.vue";
import LeArticleHoverableArea from "./LeArticleHoverableArea.vue";
import LeArticleInflections from "./LeArticleInflections.vue";
import LeArticleInflectionScheme from "./LeArticleInflectionScheme.vue";
import LeArticleInlineInflectionSchemeButton from "./LeArticleInlineInflectionSchemeButton.vue";
import LeArticlePronunciationButton from "./LeArticlePronunciationButton.vue";
import LeArticleTranslation from "./LeArticleTranslation.vue";
import LeArticleTranslationExamples from "./LeArticleTranslationExamples.vue";
import LeArticleWordclass from "./LeArticleWordclass.vue";

const props = defineProps<{
  usageLanguage: DictsysArticle["usageLanguage"];
  sourceLanguage: string;
  targetLanguage: string;
  lemmaWordclass: DictsysWordclass;
  number?: number;
  meaning: DictsysMeaning;
  indexCombinationIds: number[];
}>();

const dictsysInflectionSchemeStore = useDictsysInflectionSchemeStore();
const { isAnySchemeOpen, isSchemeOpen } = dictsysInflectionSchemeStore;

interface TranslationGroup {
  key: string;
  hasMultipleTranslations: boolean;
  translations: DictsysTranslation[];
}

const translations = computed<DictsysTranslation[]>(() => [
  ...props.meaning.translations,
  ...props.meaning.combinations
]);

const translationGroups = computed<TranslationGroup[]>(() =>
  translations.value.reduce<TranslationGroup[]>((groups, translation, index) => {
    const combinationCustomValues = getSpecificCustomValues(translation, "combination");

    const keyPrefix = combinationCustomValues.find((customValue) => customValue.position === "before")?.text ?? "";
    const keySuffix = combinationCustomValues.find((customValue) => customValue.position === "after")?.text ?? "";
    const key = `${keyPrefix}___${translation.combination ?? index}___${keySuffix}`;

    const existingIndex = groups.findIndex((group) => group.key === key);

    if (existingIndex !== -1) {
      groups[existingIndex].translations.push(translation);
      groups[existingIndex].hasMultipleTranslations = true;
      return groups;
    }

    return [...groups, { key, hasMultipleTranslations: false, translations: [translation] }];
  }, [])
);

const getSpecificCustomValues = (translation: DictsysTranslation, type: DictsysCustomValueType): DictsysCustomValue[] =>
  translation.customValues.filter((c) => c.type === type);

const getWordclass = (wordclass: DictsysWordclass): DictsysWordclass =>
  wordclass.key ? wordclass : props.lemmaWordclass;

const isGroupHighlighted = (group: TranslationGroup): boolean => {
  const combinationIds = group.translations
    .filter((translation) => translation.combination)
    .map((translation) => translation.id);

  return (
    props.indexCombinationIds.length > 0 &&
    combinationIds.some((combinationId) => props.indexCombinationIds.includes(combinationId))
  );
};

const shouldHideHoverableAreaBottom = (group: TranslationGroup): boolean => {
  const lastTranslationIndex = group.translations.length - 1;
  const lastTranslation = group.translations[lastTranslationIndex];

  return lastTranslation.examples.length === 0 && isSchemeOpen(lastTranslation.id.toString());
};

const shouldHideTranslationGroupBottom = (translationIndex: number, group: TranslationGroup): boolean => {
  const translation = group.translations[translationIndex];

  return translationIndex === group.translations.length - 1 && isSchemeOpen(translation.id.toString());
};
</script>

<template>
  <div class="le-article-meaning">
    <div
      v-if="number"
      class="le-article-meaning__definition-container"
      :class="{ 'le-article-meaning__definition-container--top-space': props.number && props.number > 1 }"
      :lang="props.usageLanguage"
    >
      <div class="le-article-meaning__number">{{ props.number }}</div>
      <span class="le-article-meaning__definition">{{ props.meaning.definition }}</span>
    </div>

    <div :class="{ 'le-article-meaning__indentation': number }">
      <ul class="le-article-meaning__list">
        <li
          v-for="(group, groupIndex) in translationGroups"
          :key="group.key"
          class="le-article-meaning__list-bullet"
          :class="{ 'le-article-meaning__list-bullet--last': groupIndex === translationGroups.length - 1 }"
        >
          <LeArticleHoverableArea
            :is-bordered="isAnySchemeOpen(group.translations.map((t) => t.id.toString()))"
            :is-highlighted="isGroupHighlighted(group)"
            class="le-article-meaning__hoverable-area"
            :class="{ 'le-article-meaning__hoverable-area--no-bottom': shouldHideHoverableAreaBottom(group) }"
          >
            <span v-if="group.translations[0].combination">
              <LeArticleCustomContent
                :values="getSpecificCustomValues(group.translations[0], 'combination')"
                class="le-article-meaning__text le-article-meaning__text--primary le-article-meaning__text--bold"
                :lang="props.sourceLanguage"
              >
                <span
                  :lang="props.sourceLanguage"
                  class="le-article-meaning__text le-article-meaning__text--primary le-article-meaning__text--bold"
                >
                  {{ group.translations[0].combination }}
                </span>
              </LeArticleCustomContent>
              <span class="le-article-meaning__text le-article-meaning__text--primary le-article-meaning__text--bold"
                >&raquo;</span
              >
            </span>

            <div
              v-for="(translation, translationIndex) in group.translations"
              :key="translationIndex"
              class="le-article-meaning__translation-group"
              :class="{
                'le-article-meaning__translation-group--blocked': group.hasMultipleTranslations,
                'le-article-meaning__translation-group--last':
                  group.hasMultipleTranslations && shouldHideTranslationGroupBottom(translationIndex, group),
                'le-article-meaning__translation-group--no-examples':
                  group.hasMultipleTranslations && translation.examples.length === 0
              }"
            >
              <span
                v-if="translation.metadata.categories.length > 0"
                class="le-article-meaning__text le-article-meaning__text--contrast"
                :lang="props.usageLanguage"
                >({{ translation.metadata.categories.join(", ") }})</span
              >

              <LeArticleCustomContent
                v-if="translation.explanation"
                :values="getSpecificCustomValues(translation, 'explanation')"
                class="le-article-meaning__text le-article-meaning__text--contrast"
                :lang="props.usageLanguage"
              >
                <span class="le-article-meaning__text le-article-meaning__text--contrast" :lang="props.usageLanguage"
                  >({{ translation.explanation }})</span
                >
              </LeArticleCustomContent>

              <span
                v-if="translation.metadata.usages.length > 0"
                class="le-article-meaning__text le-article-meaning__text--contrast"
                :lang="props.usageLanguage"
                >({{ translation.metadata.usages.join(", ") }})</span
              >
              <span
                v-if="translation.metadata.varieties.length > 0"
                class="le-article-meaning__text le-article-meaning__text--contrast"
                :lang="props.usageLanguage"
                >({{ translation.metadata.varieties.join(", ") }})</span
              >

              <LeArticleCustomContent
                v-if="translation.translation"
                :values="getSpecificCustomValues(translation, 'translation')"
                class="le-article-meaning__text le-article-meaning__text--bold"
                :lang="props.targetLanguage"
              >
                <LeArticleTranslation
                  :translation="translation.translation"
                  :usage-language="props.usageLanguage"
                  class="le-article-meaning__text le-article-meaning__text--bold le-article-meaning__text--narrow"
                  :lang="props.targetLanguage"
                />
              </LeArticleCustomContent>

              <LeArticleWordclass
                class="le-article-meaning__text le-article-meaning__text--neutral le-article-meaning__text--italic"
                :wordclass="translation.wordclass"
                :usage-language="props.usageLanguage"
              />

              <LeArticleCustomContent
                class="le-article-meaning__text le-article-meaning__text--neutral"
                :values="getSpecificCustomValues(translation, 'gender')"
                :lang="props.targetLanguage"
              >
                <LeArticleGenders
                  class="le-article-meaning__text le-article-meaning__text--neutral"
                  :genders="translation.metadata.genders"
                  :display-language="props.targetLanguage"
                  :usage-language="props.usageLanguage"
                />
              </LeArticleCustomContent>

              <LeArticleInflections
                v-if="!translation.inflectionsHidden"
                class="le-article-meaning__text"
                :inflections="translation.inflections"
                :usage-language="props.usageLanguage"
                :target-language="props.targetLanguage"
              />

              <LeArticleInlineInflectionSchemeButton
                class="le-article-meaning__text"
                :language="props.targetLanguage"
                :head-word="translation.translation"
                :wordclass="getWordclass(translation.wordclass)"
                :inflection-scheme="translation.inflectionScheme"
                :scheme-id="translation.id.toString()"
                :usage-language="props.usageLanguage"
              />

              <LeArticlePronunciationButton
                v-if="translation.pronunciationFileUrl"
                class="le-article-meaning__pronunciation-button"
                :pronunciation-file-url="translation.pronunciationFileUrl"
                :usage-language="props.usageLanguage"
                type="translation"
              />

              <LeArticleCustomContent
                v-if="translation.grammaticalComment"
                :values="getSpecificCustomValues(translation, 'gram_comment')"
                class="le-article-meaning__text le-article-meaning__text--italic"
                :lang="props.usageLanguage"
              >
                <span class="le-article-meaning__text le-article-meaning__text--italic" :lang="props.usageLanguage"
                  >({{ translation.grammaticalComment }})</span
                >
              </LeArticleCustomContent>

              <LeArticleInflectionScheme
                :scheme-id="translation.id.toString()"
                :language="props.targetLanguage"
                class="le-article-meaning__inflection-scheme"
                :class="{ 'le-article-meaning__inflection-scheme--no-examples': translation.examples.length === 0 }"
              />

              <LeArticleTranslationExamples
                :usage-language="props.usageLanguage"
                :source-language="props.sourceLanguage"
                :target-language="props.targetLanguage"
                :examples="translation.examples"
                class="le-article-meaning__examples"
              />
            </div>
          </LeArticleHoverableArea>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";
@import "@/assets/styles/dictsys-article";

.le-article-meaning {
  &__definition-container {
    display: inline-flex;
    margin-bottom: 10px;

    &--top-space {
      margin-top: 20px;
    }
  }

  &__number {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 23px;
    height: 23px;
    margin-right: 12px;
    border-radius: 50%;
    background-color: @colors[@secondary][800];
    color: @colors[@neutrals][100];
    .dictsys-article-text();
    .dictsys-article-text--bold();

    #media-breakpoint-up(md, {
      width: 26px;
      height: 26px;
    });
  }

  &__definition {
    color: @colors[@secondary][800];
    .dictsys-article-text();
    .dictsys-article-text--bold();
  }

  &__indentation {
    margin-left: 38px;
  }

  &__list {
    display: flex;
    flex-direction: column;
    margin: 0;
    padding-left: 15px;
    color: @colors[@secondary][800];
    font-size: 17px;
  }

  &__list-bullet {
    margin-bottom: 16px;
    margin-left: 4px;

    #media-breakpoint-up(md, {
      margin-bottom: 20px;
    });

    // Firefox-specific offset, as Blink and Gecko treat font growth for bullet points differently
    @-moz-document url-prefix() {
      margin-left: 0;
      padding-left: 4px;
    }

    &--last {
      margin-bottom: 0;
    }
  }

  &__hoverable-area {
    .dictsys-article-text();

    &--no-bottom {
      padding-bottom: 0;
    }
  }

  &__translation-group {
    display: inline;

    &--blocked {
      display: block;
      margin-top: 8px;
      padding-bottom: 10px;

      #media-breakpoint-up(md, {
        padding-bottom: 12px;
      });
    }

    &--no-examples {
      padding-bottom: 6px;

      #media-breakpoint-up(md, {
        padding-bottom: 8px;
      });
    }

    &--last {
      padding-bottom: 0;
    }
  }

  &__pronunciation-button {
    top: 2px;
    margin-right: 8px;
  }

  ::v-deep(&__text) {
    margin-right: 8px;
    color: @colors[@neutrals][500];

    &--contrast {
      color: rgb(0, 46, 166);
    }

    &--neutral {
      color: @colors[@neutrals][200];
    }

    &--primary {
      color: @colors[@primary][700];
    }

    &--bold {
      .dictsys-article-text--bold();
    }

    &--italic {
      font-style: italic;
    }

    &--narrow {
      margin-right: 6px;
    }
  }

  &__inflection-scheme {
    margin-top: 6px;
    margin-right: -7px;
    margin-left: @dictsys-article-inflection-scheme__left-spacing;

    #media-breakpoint-up(md, {
      margin-top: 8px;
    });

    &--no-examples {
      margin-bottom: -1px;
    }
  }

  &__examples {
    margin-top: 8px;
  }
}
</style>
